// src/lib/http.ts: same-origin JSON requests and the API error type (spec 2.6). No base URL: the SPA and
// the API always share an origin.
import type { ApiErrorBody, ApiErrorCode } from "../../shared/api";

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: ApiErrorCode | "network",
    message: string,
    public fields?: Record<string, string>,
    public retryAfterSec?: number,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export type RequestOptions = { signal?: AbortSignal; headers?: Record<string, string> };

/** Turns a non-2xx response into an ApiError, reading the envelope when there is one. */
export async function toApiError(res: Response): Promise<ApiError> {
  let body: Partial<ApiErrorBody> | null = null;
  try {
    body = (await res.json()) as Partial<ApiErrorBody>;
  } catch {
    body = null;
  }
  const e = body?.error;
  const retryHeader = Number(res.headers.get("retry-after"));
  const retryAfterSec = e?.retryAfterSec ?? (Number.isFinite(retryHeader) && retryHeader > 0 ? retryHeader : undefined);
  return new ApiError(res.status, e?.code ?? "server_error", e?.message ?? `Request failed (${res.status}).`, e?.fields, retryAfterSec);
}

/** Sends JSON (when `body` is given) and parses a JSON reply. A network failure is ApiError(0, "network"). */
export async function request<T>(method: string, path: string, body?: unknown, opts?: RequestOptions): Promise<T> {
  const headers: Record<string, string> = { accept: "application/json", ...(opts?.headers ?? {}) };
  const init: RequestInit = { method, headers, credentials: "same-origin" };
  if (opts?.signal) init.signal = opts.signal;
  if (body instanceof FormData) {
    // The browser sets the multipart content type, with its boundary.
    init.body = body;
  } else if (body instanceof Blob) {
    // A file as the body (a home photo): its own type, and the browser sets the length.
    headers["content-type"] = body.type;
    init.body = body;
  } else if (body !== undefined) {
    headers["content-type"] = "application/json";
    init.body = JSON.stringify(body);
  }
  let res: Response;
  try {
    res = await fetch(path, init);
  } catch (e) {
    if (e instanceof DOMException && e.name === "AbortError") throw e;
    throw new ApiError(0, "network", "We couldn't reach the server. Check your connection and try again.");
  }
  if (!res.ok) throw await toApiError(res);
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}
